import { confirmDialog, showDialog } from '@/utils/dialogs';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { View, ScrollView, RefreshControl, StyleSheet, Platform, TouchableOpacity } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { goBackOr } from '@/navigation/inAppHistory';
import { ROUTES } from '@/navigation/routes';
import { SafeAreaView } from 'react-native-safe-area-context';
import { MaterialIcons } from '@expo/vector-icons';

// Design system reusables (no Tailwind)
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import SwiftUIGlassCard from '@/components/ui/SwiftUIGlassCard';

import { apiRequest } from '../../services/apiClient';
import { useTranslation } from '../../hooks/useTranslation';
import { brand } from '../../design-system/theme/tokens';

type PendingInvitation = {
  id: number;
  email: string;
  full_name: string;
  invited_by?: number | null;
  invitation_expires: string;
  is_accepted: boolean;
  created_at: string;
};

type Student = {
  id: number;
  email: string;
  full_name: string;
  is_active: boolean;
  invitation_pending?: boolean | null;
};

export default function SchoolInvitationsScreenEnhanced() {
  const { t } = useTranslation();
  const router = useRouter();
  const params = useLocalSearchParams<{ schoolId?: string | string[] }>();
  const schoolId = Array.isArray(params.schoolId) ? Number(params.schoolId[0]) : Number(params.schoolId);

  const [refreshing, setRefreshing] = useState(false);
  const [pendingInvites, setPendingInvites] = useState<PendingInvitation[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [working, setWorking] = useState<{ key: string; id: number } | null>(null);

  const loadData = useCallback(async () => {
    if (!schoolId) return;
    try {
      const [invites, studentsResp] = await Promise.all([
        apiRequest<PendingInvitation[]>({ endpoint: `/schools/${schoolId}/student-invitations?status=pending` }),
        apiRequest<Student[]>({ endpoint: `/schools/${schoolId}/students` }),
      ]);
      setPendingInvites(invites || []);
      setStudents(studentsResp || []);
    } catch (e: any) {
      showDialog(t('common.error'), e?.message || 'Failed to load invitations');
    }
  }, [schoolId, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await loadData();
    } finally {
      setRefreshing(false);
    }
  };

  // Legacy user-hash invitations: inactive students with invitation_pending=true
  const legacyPending = useMemo(
    () => (students || []).filter((s) => !s.is_active && !!s.invitation_pending),
    [students]
  );

  const resendInvite = async (invitationId: number) => {
    try {
      setWorking({ key: 'resend', id: invitationId });
      await apiRequest({ endpoint: `/schools/${schoolId}/student-invitations/${invitationId}/resend`, method: 'POST' });
      await loadData();
      showDialog(t('invitations.actions.resentTitle'), t('invitations.actions.resentBody'));
    } catch (e: any) {
      showDialog('Error', e?.message || 'Failed to resend invitation');
    } finally {
      setWorking(null);
    }
  };

  const cancelInvite = async (invitationId: number) => {
    const confirmed = await confirmDialog(
      t('invitations.actions.cancel'),
      t('invitations.actions.cancelConfirm'),
      t('common.yes'),
      t('common.no'),
    );
    if (!confirmed) return;

    try {
      setWorking({ key: 'cancel', id: invitationId });
      await apiRequest({ endpoint: `/schools/${schoolId}/student-invitations/${invitationId}`, method: 'DELETE' });
      await loadData();
    } catch (e: any) {
      showDialog('Error', e?.message || 'Failed to cancel invitation');
    } finally {
      setWorking(null);
    }
  };

  const resendLegacyInvite = async (studentId: number) => {
    try {
      setWorking({ key: 'resend-legacy', id: studentId });
      await apiRequest({ endpoint: `/schools/${schoolId}/students/${studentId}/resend-invite`, method: 'POST' });
      await loadData();
      showDialog(t('invitations.actions.resentTitle'), t('invitations.actions.resentBody'));
    } catch (e: any) {
      showDialog('Error', e?.message || 'Failed to resend student invitation');
    } finally {
      setWorking(null);
    }
  };

  const SectionCard: React.FC<{ title: string; children: React.ReactNode }[]> | any = ({ title, children }: any) => {
    // Use SwiftUI glass where available; fall back to Card
    if (Platform.OS === 'ios') {
      return (
        <SwiftUIGlassCard style={styles.card}>
          <Text style={styles.cardTitle}>{title}</Text>
          <View style={styles.divider} />
          {children}
        </SwiftUIGlassCard>
      );
    }
    return (
      <Card style={styles.card}>
        <CardHeader>
          <Text style={styles.cardTitle}>{title}</Text>
        </CardHeader>
        <CardContent>{children}</CardContent>
      </Card>
    );
  };

  const recount = async () => {
    try {
      setWorking({ key: 'recount', id: schoolId || 0 });
      const res = await apiRequest<{ previous_count: number; new_count: number; school_slug: string }>({
        endpoint: `/schools/${schoolId}/recount-students`,
        method: 'POST',
      });
      showDialog(t('invitations.actions.recountDone'), t('invitations.actions.recountFromTo', { prev: res.previous_count, next: res.new_count }));
      await loadData();
    } catch (e: any) {
      showDialog('Recount Failed', e?.message || 'Unable to recount students');
    } finally {
      setWorking(null);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      {/* Header with Back Button */}
      <View style={styles.header}>
        <TouchableOpacity
          onPress={() => goBackOr(router, ROUTES.TABS_SCHOOL_MANAGEMENT)}
          style={styles.backButton}
          accessibilityRole="button"
          accessibilityLabel={t('common.back')}
        >
          <MaterialIcons name="arrow-back" size={24} color={brand.primary} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{t('invitations.title')}</Text>
      </View>

      <ScrollView
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
        contentContainerStyle={styles.content}
      >
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <Text style={styles.screenTitle}>{t('invitations.pendingSection', { count: pendingInvites.length })}</Text>
          <Button onPress={recount} disabled={working?.key === 'recount'}>
            <Text style={styles.buttonText}>{t('invitations.actions.recount')}</Text>
          </Button>
        </View>

        <SectionCard title={t('invitations.pendingSection', { count: pendingInvites.length })}>
          {pendingInvites.length === 0 ? (
            <Text style={styles.muted}>{t('invitations.noPending')}</Text>
          ) : (
            pendingInvites.map((inv) => (
              <View key={`inv-${inv.id}`} style={styles.row}>
                <View style={styles.rowText}>
                  <Text style={styles.rowTitle}>{inv.full_name || inv.email}</Text>
                  <Text style={styles.rowSub}>{inv.email}</Text>
                  <Text style={styles.rowMeta}>{t('invitations.expires', { date: new Date(inv.invitation_expires).toLocaleString() })}</Text>
                </View>
                <View style={styles.rowActions}>
                  <Button
                    onPress={() => resendInvite(inv.id)}
                    disabled={working?.key === 'resend' && working?.id === inv.id}
                  >
                    <Text style={styles.buttonText}>{t('invitations.actions.resend')}</Text>
                  </Button>
                  <View style={{ width: 12 }} />
                  <Button
                    onPress={() => cancelInvite(inv.id)}
                    disabled={working?.key === 'cancel' && working?.id === inv.id}
                  >
                    <Text style={styles.buttonText}>{t('invitations.actions.cancel')}</Text>
                  </Button>
                </View>
              </View>
            ))
          )}
        </SectionCard>

        <SectionCard title={t('invitations.legacySection', { count: legacyPending.length })}>
          {legacyPending.length === 0 ? (
            <Text style={styles.muted}>{t('invitations.noLegacy')}</Text>
          ) : (
            legacyPending.map((s) => (
              <View key={`legacy-${s.id}`} style={styles.row}>
                <View style={styles.rowText}>
                  <Text style={styles.rowTitle}>{s.full_name || s.email}</Text>
                  <Text style={styles.rowSub}>{s.email}</Text>
                  <Text style={styles.rowMeta}>{t('invitations.awaiting')}</Text>
                </View>
                <View style={styles.rowActions}>
                  <Button
                    onPress={() => resendLegacyInvite(s.id)}
                    disabled={working?.key === 'resend-legacy' && working?.id === s.id}
                  >
                    <Text style={styles.buttonText}>{t('invitations.actions.resend')}</Text>
                  </Button>
                </View>
              </View>
            ))
          )}
        </SectionCard>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F8FAFC' },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  backButton: {
    padding: 8,
    marginRight: 8,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#0F172A',
  },
  content: { padding: 16 },
  screenTitle: { fontSize: 18, fontWeight: '600', color: '#0F172A' },
  card: { marginBottom: 16 },
  cardTitle: { fontSize: 18, fontWeight: '600', color: '#0F172A' },
  divider: { height: 1, backgroundColor: 'rgba(0,0,0,0.08)', marginVertical: 8 },
  muted: { color: '#64748B', fontSize: 14, paddingVertical: 8 },
  row: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 12 },
  rowText: { flex: 1, paddingRight: 12 },
  rowTitle: { fontSize: 16, fontWeight: '600', color: '#0F172A' },
  rowSub: { fontSize: 14, color: '#334155', marginTop: 2 },
  rowMeta: { fontSize: 12, color: '#64748B', marginTop: 2 },
  rowActions: { flexDirection: 'row', alignItems: 'center' },
  buttonText: { color: '#ffffff', fontWeight: '600' },
});
